<script setup lang="ts">
import * as yup from 'yup';
import { useForm } from 'vee-validate';
import { modelMapper } from '~/models';

useTitle('users.sign_up', { t: true });
useDescription('Start your free 14 day trial today!');

definePageMeta({
  middleware: 'check-no-auth',
});

const { t } = useI18n();
const { region } = useRegion();
const { gtag } = useAnalytics();

interface State {
  showReferrerField: boolean;
  validateOnly: boolean;
  canSubmit: boolean;
}

const state: State = reactive({
  showReferrerField: false,
  validateOnly: region === 'china', // If region is China, validate only first
  canSubmit: true,
});

const apiClient = useAPIClient();
const referrer = useCookie('referrer');

const signUpSchema = useSchema(
  yup.object({
    username: yup
      .string()
      .required()
      .min(5)
      .max(20)
      .meta({ label: t('users.username') }),
    email: yup
      .string()
      .email()
      .required()
      .meta({ label: t('users.email') }),
    password: yup
      .string()
      .required()
      .min(10)
      .max(36)
      .matches(/[a-z]/, t('errors.forms.password_lowercase'))
      .matches(/[A-Z]/, t('errors.forms.password_uppercase'))
      .matches(/\d/, t('errors.forms.password_number'))
      .matches(/\W/, t('errors.forms.password_special'))
      .meta({ label: t('users.password') }),
    passwordConfirm: yup
      .string()
      .required()
      .oneOf([yup.ref('password')], t('errors.forms.passwords_match'))
      .meta({ label: t('users.confirm_password') }),
    referrer: yup.string().meta({ label: t('users.referrer') }),
    countryCode: yup
      .string()
      .max(4)
      .default('+86')
      .meta({ label: t('mobile_otp.country_code') })
      .when([], {
        is: () => region === 'china',
        then: (f) => f.required(),
      }),
    mobileNumber: yup
      .string()
      .min(10)
      .meta({ label: t('mobile_otp.mobile_number') })
      .when([], {
        is: () => region === 'china',
        then: (f) => f.required().min(10),
      }),
    mobileCode: yup
      .string()
      .length(5)
      .meta({ label: t('mobile_otp.code') })
      .when([], {
        is: () => region === 'china' && !state.validateOnly,
        then: (f) => f.required(),
      }),
  })
);

const {
  handleSubmit,
  values,
  errors,
  setFieldError,
  setFieldValue,
  isSubmitting,
} = useForm({
  validationSchema: signUpSchema,
  initialErrors: { referrer: '' },
});

if (referrer.value) {
  setFieldValue('referrer', referrer.value);
}

const mobileCodeResendDelayed = async (delayed: boolean) => {
  if (delayed) {
    state.validateOnly = true;
    state.canSubmit = false;
  } else {
    state.canSubmit = true;
  }
};

const onSubmit = handleSubmit(async (values) => {
  const params: Partial<typeof values> = {
    username: values.username,
    email: values.email,
    password: values.password,
    referrer: values.referrer,
    validateOnly: state.validateOnly,
  };

  // For China verification
  if (values.mobileNumber) {
    params.mobileNumber = values.countryCode + values.mobileNumber;
    if (!state.validateOnly) params.mobileCode = values.mobileCode;
  }

  try {
    const data = await apiClient.fetch('users/sign_up', {
      method: 'POST',
      body: params,
    });

    if (state.validateOnly) {
      state.validateOnly = false;
      return;
    }

    // Remove referrer cookie
    referrer.value = undefined;

    const tokenPair = modelMapper.TokenPair(data);
    const userState = useUserState();
    userState.value.tokenPair = tokenPair;

    gtag('event', 'sign_up');
    return navigateTo('/users');
  } catch (err: any) {
    apiClient.setFormErrors(setFieldError, 'users', err);
    // Remove cookie if referrer is invalid
    if (errors.value.referrer) {
      referrer.value = undefined;
    }
  }
});
</script>

<template>
  <SessionTabs>
    <div class="sign-up page">
      <div class="content">
        <div class="features">
          <span class="headline">
            {{ $t('sign_up.headline') }}
          </span>
          <ul>
            <li>{{ $t('sign_up.feature_1') }}</li>
            <li>{{ $t('sign_up.feature_2') }}</li>
            <li>{{ $t('sign_up.feature_3') }}</li>
            <li>{{ $t('sign_up.feature_4') }}</li>
          </ul>
        </div>

        <FormBase class="sign-up-form">
          <ErrorMessage name="referrer" />
          <form @submit="onSubmit" class="form">
            <fieldset :disabled="region === 'china' && !state.validateOnly">
              <FormInput :label="$t('users.username')" name="username" type="text" />
              <FormInput :label="$t('users.email')" name="email" type="text" />
              <FormInput :label="$t('users.password')" name="password" type="password" />
              <FormInput :label="$t('users.confirm_password')" name="passwordConfirm" type="password" />

              <FormInput v-show="state.showReferrerField || errors.referrer"
                :label="`${$t('users.referrer')} (${$t('common.optional')})`" name="referrer" />

              <div class="form-field">
                <a v-show="!referrer && !state.showReferrerField" @click.prevent="state.showReferrerField = true"
                  href="#" class="referrer-toggle">
                  {{ $t('users.referrer_toggle') }}
                </a>
                <div v-if="referrer" class="referrer subdued">
                  {{ $t('users.referrer') }}: {{ referrer }}
                </div>
              </div>
            </fieldset>

            <FormMobileOTP v-if="region === 'china'" :show-code-input="!state.validateOnly"
              @code-resend-delayed="mobileCodeResendDelayed" />

            <Captcha :show="apiClient.showCaptcha.value"
              @verified="(p: string) => apiClient.captchaPayload.value = p" />

            <div class="actions">
              <button class="button" type="submit" :disabled="isSubmitting || !state.canSubmit">
                {{ $t('users.sign_up') }}
              </button>
            </div>
          </form>
        </FormBase>
      </div>
    </div>
  </SessionTabs>
</template>

<style scoped lang="scss">
.content {
  @media (min-width: $breakpoint-sm) {
    display: flex;
    width: 80%;
    margin: 0 auto;

    .features {
      flex: 1 1 45%;
    }

    .sign-up-form {
      flex: 1 1 55%;

      fieldset {
        margin-bottom: 1rem;
        padding: 0;
        border: none;
      }
    }
  }
}

.features {
  padding-right: 3rem;

  .headline {
    font-size: 1.5rem;
    font-weight: bold;
  }
}

.referrer {
  margin-top: 0.5rem;
  text-align: right;
  font-size: 0.85rem;
}
</style>
